import React from "react";
import { Box, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import { useNavigate } from "react-router-dom";
import { formatCurrencyManual } from "../../../utils/formatters";
import { OrderSkeleton } from "../../../components/spinner/loader";
import { FormCard } from "../../../components/ui/form";
import { Num } from "../../../components/ui";
import RowAction, { RowActions } from "../../../components/ui/RowAction";
import { IconPen, IconCheckCircle, IconPlus } from "../../../components/ui/icons";
import { tokens } from "../../../theme";

/*
  En escritorio entra la grilla completa. En teléfono se muestran sólo orden,
  descripción, total y acciones: los tres importes de IVA se leen en el detalle
  de la orden y acá obligaban a scrollear de lado.
*/
const GRID = {
  display: "grid",
  gridTemplateColumns: {
    xs: "76px minmax(0, 1fr) 92px 72px",
    md: "92px 104px minmax(0, 1fr) 104px 104px 108px 116px 72px",
  },
  alignItems: "center",
  gap: { xs: "8px", md: "10px" },
};

const soloEscritorio = { display: { xs: "none", md: "block" } };

const ReadyForBillOrders = ({
  readyForBillOrders,
  client,
  selectedOrdersForFacturation,
  setSelectedOrdersForFacturation,
  setTotalExpenses,
  setTotalExpensesWithIva,
  setTotalIva105,
  setTotalIva210,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const updateTotals = (orders) => {
    const totalExpenses = orders.reduce(
      (acc, item) => acc + Number(item.subTotal),
      0
    );
    const totalIva105 = orders.reduce(
      (acc, item) => acc + Number(item.iva105),
      0
    );
    const totalIva210 = orders.reduce(
      (acc, item) => acc + Number(item.iva210),
      0
    );
    const totalWithIva = orders.reduce(
      (acc, item) =>
        acc + Number(item.subTotal) + Number(item.iva105) + Number(item.iva210),
      0
    );

    setTotalExpenses(totalExpenses);
    setTotalExpensesWithIva(totalWithIva);
    setTotalIva105(totalIva105);
    setTotalIva210(totalIva210);
  };

  const handleSelectOrder = (item) => {
    const updatedOrders = [...selectedOrdersForFacturation, item];
    setSelectedOrdersForFacturation(updatedOrders);
    updateTotals(updatedOrders);
  };

  const handleDeselectOrder = (item) => {
    const updatedOrders = selectedOrdersForFacturation.filter(
      (prevItem) => !isEqual(prevItem, item)
    );
    setSelectedOrdersForFacturation(updatedOrders);
    updateTotals(updatedOrders);
  };

  const seleccionadas = selectedOrdersForFacturation.length;

  return (
    <FormCard
      title="Órdenes listas para facturar"
      subtitle={
        isLoading
          ? "Cargando…"
          : seleccionadas > 0
            ? `${seleccionadas} de ${readyForBillOrders.length} seleccionadas`
            : `${readyForBillOrders.length} ${
                readyForBillOrders.length === 1 ? "orden disponible" : "órdenes disponibles"
              }`
      }
      sx={{ width: "100%", minWidth: 0 }}
    >
      {isLoading ? (
        <OrderSkeleton />
      ) : !readyForBillOrders?.length ? (
        <Typography sx={{ fontSize: "12.5px", color: tokens.ink3, paddingY: "10px" }}>
          Este cliente no tiene órdenes listas para facturar.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            sx={{
              ...GRID,
              paddingBottom: "8px",
              borderBottom: `1px solid ${tokens.border}`,
              "& > *": {
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: tokens.ink3,
              },
            }}
          >
            <Typography>Orden</Typography>
            <Typography sx={soloEscritorio}>Fecha</Typography>
            <Typography>Descripción</Typography>
            <Typography sx={{ ...soloEscritorio, textAlign: "right" }}>IVA 10,5%</Typography>
            <Typography sx={{ ...soloEscritorio, textAlign: "right" }}>IVA 21%</Typography>
            <Typography sx={{ ...soloEscritorio, textAlign: "right" }}>Sub total</Typography>
            <Typography sx={{ textAlign: "right" }}>Total</Typography>
            <Typography sx={{ textAlign: "right" }}>Acciones</Typography>
          </Box>

          {readyForBillOrders.map((item) => {
            const isPlaceholder = item.description === "Cargando...";
            const isSelected = selectedOrdersForFacturation.some((selectedItem) =>
              isEqual(selectedItem, item)
            );
            const total = Number(item.subTotal) + Number(item.iva105) + Number(item.iva210);

            return (
              <Box
                key={item.orderId}
                sx={{
                  ...GRID,
                  paddingY: "10px",
                  paddingX: { xs: "6px", md: "8px" },
                  marginX: { xs: "-6px", md: "-8px" },
                  borderRadius: `${tokens.radiusControl}px`,
                  borderBottom: `1px solid ${tokens.borderSoft}`,
                  // La fila elegida se tiñe: es la que va a entrar en la factura.
                  backgroundColor: isSelected ? tokens.brandTint : "transparent",
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                <Num sx={{ fontSize: "13px", fontWeight: 600 }}>#{item.orderId}</Num>
                <Num sx={{ ...soloEscritorio, fontSize: "12.5px", color: tokens.ink2 }}>
                  {item.date}
                </Num>
                <Typography
                  sx={{
                    fontSize: "12.5px",
                    minWidth: 0,
                    color: isPlaceholder ? tokens.ink4 : tokens.ink,
                    fontStyle: isPlaceholder ? "italic" : "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={item.description}
                >
                  {item.description}
                </Typography>
                <Num sx={{ ...soloEscritorio, fontSize: "12.5px", color: tokens.ink2, textAlign: "right" }}>
                  {formatCurrencyManual(item.iva105)}
                </Num>
                <Num sx={{ ...soloEscritorio, fontSize: "12.5px", color: tokens.ink2, textAlign: "right" }}>
                  {formatCurrencyManual(item.iva210)}
                </Num>
                <Num sx={{ ...soloEscritorio, fontSize: "12.5px", color: tokens.ink2, textAlign: "right" }}>
                  {formatCurrencyManual(item.subTotal)}
                </Num>
                <Num sx={{ fontSize: "13px", fontWeight: 600, textAlign: "right" }}>
                  {formatCurrencyManual(total)}
                </Num>
                <RowActions>
                  {!isPlaceholder && (
                    <>
                      <RowAction
                        icon={IconPen}
                        title="Editar orden"
                        onClick={() => {
                          navigate("/order-details-form", {
                            state: { order: { ...item, client: client } },
                          });
                        }}
                      />
                      {isSelected ? (
                        <RowAction
                          icon={IconCheckCircle}
                          title="Quitar de la factura"
                          onClick={() => handleDeselectOrder(item)}
                        />
                      ) : (
                        <RowAction
                          icon={IconPlus}
                          title="Agregar a la factura"
                          onClick={() => handleSelectOrder(item)}
                        />
                      )}
                    </>
                  )}
                </RowActions>
              </Box>
            );
          })}
        </Box>
      )}
    </FormCard>
  );
};

export default ReadyForBillOrders;
